import { apiCache } from './apiCache';
import { resetBrandingOnLogout } from '../hooks/useBranding';
import { resetProductOnboardingOnLogout } from '../ui/ProductOnboardingProvider';
import { resetAnalytics } from './analytics';
import { clearOnboardingBrowserStateOnLogout } from './onboardingLocalStorage';

export function getToken(): string | null {
  try {
    return window.localStorage.getItem('klaris_jwt');
  } catch {
    return null;
  }
}

export function parseJwt(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || typeof payload !== 'object') return true;
  const expSec = Number(payload.exp || 0);
  if (!expSec) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec >= expSec;
}

/**
 * Signal that the signed-in identity changed.
 *
 * `PermissionsProvider` sits above the Router, so a client-side navigate()
 * never remounts it — without this it keeps whatever token it read when the
 * page first loaded. That is how a freshly invited admin landed in the app
 * with an empty capability set and no Settings or People in the sidebar.
 *
 * A `storage` event is not a substitute: it fires in *other* tabs, never in
 * the one that wrote the value.
 */
export function notifyAuthChange() {
  try {
    window.dispatchEvent(new Event('auth-change'));
  } catch {
    // ignore
  }
}

/**
 * Write the signed-in identity, and say so.
 *
 * Six places used to write these three keys by hand and each had to remember
 * the event; three forgot. Going through here makes the notification the
 * default and the omission deliberate.
 *
 * Pass `notify: false` only where a mid-flow refetch would be disruptive —
 * the signup wizard writes its token several steps before it hands off to the
 * app, and calls `notifyAuthChange()` itself at the handoff.
 */
export function setAuth(
  token: string,
  user: unknown,
  tenant: unknown,
  { notify = true }: { notify?: boolean } = {},
) {
  try {
    window.localStorage.setItem('klaris_jwt', token);
    window.localStorage.setItem('klaris_user', JSON.stringify(user ?? {}));
    window.localStorage.setItem('klaris_tenant', JSON.stringify(tenant ?? {}));
  } catch {
    // ignore
  }
  if (notify) notifyAuthChange();
}

export function clearAuthStorage() {
  try {
    window.localStorage.removeItem('klaris_jwt');
    window.localStorage.removeItem('klaris_user');
    window.localStorage.removeItem('klaris_tenant');
    clearOnboardingBrowserStateOnLogout();
    // Clear API cache to prevent cross-tenant data leakage
    apiCache.clear();
    // Clear branding to prevent multi-tenant data leakage
    resetBrandingOnLogout();
    resetProductOnboardingOnLogout();
    // Reset PostHog identity so next session is anonymous
    resetAnalytics();
    // Same signal login already fires, so anything holding per-user state —
    // capabilities, most of all — drops it here rather than carrying the
    // previous person's permissions into the next session.
    notifyAuthChange();
  } catch {
    // ignore
  }
}


